// OAuth connect in a POPUP, with a redirect fallback.
//
// Why a popup at all: the connect button usually lives inside the Settings MODAL. A full-page redirect
// unmounts that modal, so the band comes back to a bare page having lost their place — and on the
// connections tab specifically, they lose the scroll position and the card they were working on. A popup
// leaves the app exactly as it was and simply repaints the card when the child reports back.
//
// The fallback matters as much as the popup: browsers block window.open when it is not clearly
// user-initiated, and some managed/mobile browsers block it always. When that happens we take the old
// full-page redirect, but ask the server to return us to the page the band was ON (plus a marker that
// reopens Settings on the connections tab), rather than dumping them at a fixed route.
//
// ── `window.opener` IS NOT A CHANNEL, AND IT IS NOT A DETECTOR (2026-09-09) ────────────────────────────
// A vendor that sends `Cross-Origin-Opener-Policy` moves the popup into a NEW browsing-context group the
// moment it loads, and the severance is PERMANENT — navigating back to our own origin does not restore it.
// Measured: `accounts.shopify.com` and `connect.squareup.com` send `same-origin`,
// `{shop}.myshopify.com/admin/oauth/authorize` sends `noopener-allow-popups`. So for Shopify the popup came
// back with `window.opener === null`, which broke this file in BOTH directions:
//
//   * the DETECTOR (`window.opener` + the callback params) said "not a popup", so the popup booted the whole
//     SPA inside a 520px window at `/` — the homepage — and never closed; and
//   * the REPORT (`opener.postMessage`) could never have been delivered anyway.
//
// Both halves are now opener-independent. Detection is OUR OWN marker on the return path — set by the same
// call that opens the popup, so a document carrying it is a popup by construction, whatever COOP did to the
// opener chain. Reporting goes over BOTH the opener and a same-origin `BroadcastChannel`, deduped by id.
//
// Those two are exactly complementary rather than a belt-and-braces guess, and that is why there is no third:
// COOP severance and BroadcastChannel arrived in the same generation of browsers (Safari 15.4 for both), so an
// engine old enough to lack the channel is one that never severs the opener, and an engine that severs always
// has the channel. `localStorage` would be the usual third leg and is banned outright here anyway.
//
// Dropping `window.opener` from the DETECTOR also closed a bug pointing the other way: on the fallback path
// the band's REAL tab carries `?connected=&status=`, so a tab that happened to have an opener (anything
// reached from a `target="_blank"` link) matched the old predicate and had `window.close()` called on it.

/** The query params the OAuth callback appends to whatever return path it was given. */
const CONNECTED_PARAM = "connected";
const STATUS_PARAM = "status";
/**
 * OUR marker, on the return path handed to the server when a popup opened. It is the whole trigger for the
 * self-close: it is set by `popupReturnPath()` and by nothing else, so it cannot appear on the band's own
 * tab, and it survives the vendor round trip because the server echoes the return path it was given.
 */
const POPUP_PARAM = "oauth_popup";
const POPUP_VALUE = "1";
/** Our own marker, added to the fallback return path so the shell knows to reopen Settings. */
export const SETTINGS_PARAM = "settings";

export interface OAuthResult {
  provider: string;
  ok: boolean;
  /** The /apply Instagram verification's draft token — the only connect that runs with no account, so the
   * popup hands its result back to the form this way. Absent for every band connect. */
  draft?: string;
  /** Why a connect failed, when the server says (`denied`, `no_instagram`). */
  reason?: string;
}

const MESSAGE_TYPE = "cntrl:oauth-complete";
/** Same-origin by construction, and — unlike `opener` — untouched by a COOP browsing-context-group switch. */
const CHANNEL_NAME = "cntrl:oauth";

interface OAuthAnnouncement {
  type: typeof MESSAGE_TYPE;
  /** Dedupes the two channels against each other — the parent acts on whichever arrives first. */
  id: string;
  result: OAuthResult;
}

function announcementId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    /* a locked-down crypto is not a reason to fail the handoff — the fallback is unique enough to dedupe */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Tell the app window what happened, over both same-origin channels. Each is wrapped on its own: a channel
 * that is absent or throws must not stop the one that would have worked, and neither is reliable alone —
 * `opener` dies to COOP, `BroadcastChannel` is absent on older Safari.
 */
function announce(result: OAuthResult): void {
  const payload: OAuthAnnouncement = { type: MESSAGE_TYPE, id: announcementId(), result };

  try {
    // Same-origin only: the callback always redirects back to our own origin, so there is never a reason to
    // broadcast this to "*" — that would hand the outcome to any window that happens to be listening.
    if (window.opener && window.opener !== window) {
      window.opener.postMessage(payload, window.location.origin);
    }
  } catch {
    /* severed or already closed — the other two channels do not need it */
  }

  try {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(payload);
      channel.close();
    }
  } catch {
    /* no channel here — the opener above was the only route, and the parent still refetches on focus */
  }
}

/**
 * Runs at boot, BEFORE React renders. If this document is an OAuth popup that has just come back from the
 * callback, report the outcome to the app window and close.
 *
 * Detection is our own `oauth_popup` marker, NOT `window.opener` — see the header. Returns true when the
 * document is a popup, so the caller renders the sign-off notice instead of booting an app nobody will see:
 * `window.close()` is best-effort, and a refusal must leave a readable page rather than the branded loader
 * spinning over nothing.
 */
export function completeOAuthPopupIfReturning(): boolean {
  if (typeof window === "undefined") return false;

  const params = new URLSearchParams(window.location.search);
  if (params.get(POPUP_PARAM) !== POPUP_VALUE) return false;

  const provider = params.get(CONNECTED_PARAM);
  // The marker without a verdict means the popup came back some way the callback did not drive (a vendor
  // bounce, a back button). Still a popup, still ours to close — just with nothing to report.
  if (provider)
    announce({
      provider,
      ok: params.get(STATUS_PARAM) === "connected",
      draft: params.get("draft") ?? undefined,
      reason: params.get("reason") ?? undefined,
    });

  window.close();
  return true;
}

/** Opener side: listen for a popup reporting its outcome, over either channel. Returns an unsubscribe. */
export function onOAuthComplete(handler: (result: OAuthResult) => void): () => void {
  // One announcement can arrive twice. The parent acts on the first and drops the rest — without this the
  // connections tab would toast "Connected to Shopify." twice for one connect.
  const seen = new Set<string>();

  const deliver = (data: unknown) => {
    const payload = data as Partial<OAuthAnnouncement> | null;
    if (payload?.type !== MESSAGE_TYPE || !payload.result?.provider) return;
    if (typeof payload.id === "string") {
      if (seen.has(payload.id)) return;
      seen.add(payload.id);
    }
    handler({
      provider: payload.result.provider,
      ok: payload.result.ok === true,
      draft: typeof payload.result.draft === "string" ? payload.result.draft : undefined,
      reason: typeof payload.result.reason === "string" ? payload.result.reason : undefined,
    });
  };

  const onMessage = (event: MessageEvent) => {
    // Trust only our own origin and our own message shape — a message event can come from anywhere.
    if (event.origin !== window.location.origin) return;
    deliver(event.data);
  };
  window.addEventListener("message", onMessage);

  // A BroadcastChannel is same-origin by construction, so it carries no origin to check.
  let channel: BroadcastChannel | null = null;
  try {
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event) => deliver(event.data);
    }
  } catch {
    channel = null;
  }

  return () => {
    window.removeEventListener("message", onMessage);
    channel?.close();
  };
}

/**
 * Open the vendor consent in a popup. Returns the window, or null when the browser blocked it — the caller
 * then falls back to a full-page redirect.
 *
 * Must be called synchronously from the click handler: a popup opened after an `await` has lost its
 * user-gesture provenance and is blocked by every major browser. That is why the caller opens a BLANK popup
 * first and navigates it once the authorize URL arrives.
 */
export function openOAuthPopup(): Window | null {
  const width = 520;
  const height = 720;
  // Centre on the window the band is actually looking at, not the primary display — a multi-monitor setup
  // otherwise pops the consent screen onto a different screen entirely.
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
  const popup = window.open(
    "about:blank",
    "cntrl-oauth",
    `popup=yes,width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)}`,
  );
  return popup ?? null;
}

/**
 * The return path for the POPUP: a bare route carrying the marker that tells the returning document it is a
 * popup and must close itself. The band never sees this page, so it is deliberately the cheapest route we
 * have rather than anywhere meaningful.
 */
export function popupReturnPath(): string {
  return `/?${POPUP_PARAM}=${POPUP_VALUE}`;
}

/** The return path for the FALLBACK redirect: where the band is now, plus the marker that reopens Settings
 * on the connections tab so they land back where they were rather than at a fixed route. It deliberately
 * carries NO popup marker — this path lands in the band's own tab, which must never close itself. */
export function fallbackReturnPath(): string {
  const url = new URL(window.location.href);
  url.searchParams.set(SETTINGS_PARAM, "connections");
  // Relative only — the server validates this as a same-origin absolute path and rejects anything else.
  return `${url.pathname}${url.search}`;
}
